import * as types from '@/store/mutation-types';
import inspectionTemplateApi from '@/store/inspection/inspectionTemplateApi';
import { logAction } from '@/store/logActions';
import { editingAllowed } from '@/store/actions';
import log from '@/logger';

export const loadProjectInspectionTemplates = async ({ dispatch, commit }, { projectId }) => {
  let list = await dispatch('getInspectionTemplates', { projectId });
  commit(types.setInspectionTemplateList, list);
  return list;
};

export const getInspectionTemplate = (ctx, id) => {
  log.debug('actions:getInspectionTemplate', id);
  return inspectionTemplateApi.getInspectionTemplate(id);
};

export const getInspectionTemplates = (ctx, { projectId }) => {
  return inspectionTemplateApi.getInspectionTemplates(projectId);
};

export const deleteInspectionTemplate = async ({ commit, state }, inspectionTemplate) => {
  log.debug('actions:deleteInspectionTemplate', inspectionTemplate);

  if (!editingAllowed(state)) {
    return;
  }
  logAction(
    { commit, state },
    { project: state.currentProject, object: inspectionTemplate, reason: 'Deleted InspectionTemplate' }
  );
  commit(types.setInspectionTemplateProperties, {
    updateInspectionTemplate: inspectionTemplate,
    newInspectionTemplateProperties: { archived: true },
  });
  await inspectionTemplateApi
    .saveInspectionTemplate(inspectionTemplate)
    .then((savedInspectionTemplate) => {
      commit(types.setInspectionTemplateProperties, {
        updateInspectionTemplate: inspectionTemplate,
        newInspectionTemplateProperties: savedInspectionTemplate,
      });
    })
    .catch((e) => {
      log.error('inspectionTemplateActions:deleteInspectionTemplate, saving InspectionTemplate', e);
      commit(types.setSyncStatus, { status: 'Error', live: 0, message: 'Database Error occurred', count: 1 });
    });
};

export const addInspectionTemplate = ({ commit, state }, { projectId, newInspectionTemplateProperties }) => {
  if (!editingAllowed(state)) {
    return;
  }

  return inspectionTemplateApi
    .createInspectionTemplate(projectId, newInspectionTemplateProperties)
    .then((inspectionTemplate) => {
      // setInspectionTemplateProperties({commit, state}, {updateInspectionTemplate: inspectionTemplate, newInspectionTemplateProperties: newInspectionTemplateProperties});
      commit(types.addInspectionTemplate, { newInspectionTemplate: inspectionTemplate });
      logAction(
        { commit, state },
        { project: state.currentProject, object: inspectionTemplate, reason: 'Added InspectionTemplate' }
      );

      return inspectionTemplate;
    })
    .catch((e) => {
      log.error('inspectionTemplateActions:addInspectionTemplate', e);
      commit(types.setSyncStatus, { status: 'Error', live: 0, message: 'Database Error occurred', count: 1 });
    });
};

export const saveInspectionSectionProperties = ({ dispatch }, itemUpdate) => {
  let update = {
    updateInspectionTemplate: itemUpdate.updateInspectionTemplate,
    newInspectionTemplateProperties: {
      sections: Object.assign([], itemUpdate.updateInspectionTemplate.sections),
    },
    reason: itemUpdate.reason,
  };

  if (!(itemUpdate.sectionIndex >= 0)) {
    itemUpdate.sectionIndex = itemUpdate.updateInspectionTemplate.sections.findIndex(
      (i) => i._id === itemUpdate.inspectionSectionId
    );
    if (itemUpdate.sectionIndex === -1) {
      throw new Error(
        `inspectionSectionId ${itemUpdate.inspectionSectionId} not found in collection ${JSON.stringify(
          itemUpdate.updateInspectionTemplate.sections,
          null,
          ' '
        )})`
      );
    }
  }

  if (itemUpdate.hasOwnProperty('newIndex')) {
    update.newInspectionTemplateProperties.sections.splice(
      itemUpdate.newIndex,
      0,
      update.newInspectionTemplateProperties.sections.splice(itemUpdate.sectionIndex, 1)[0]
    );
    for (let i = 0; i < update.newInspectionTemplateProperties.sections.length; i++) {
      update.newInspectionTemplateProperties.sections[i] = Object.assign(
        {},
        update.newInspectionTemplateProperties.sections[i],
        { sort: i }
      );
    }
  } else {
    update.newInspectionTemplateProperties.sections[itemUpdate.sectionIndex] = Object.assign(
      {},
      inspectionTemplateApi.getDefaultInspectionSection(),
      itemUpdate.updateInspectionTemplate.sections[itemUpdate.sectionIndex],
      itemUpdate.newInspectionSectionProperties
    );
  }
  return dispatch('saveInspectionTemplateProperties', update).then((e) => {
    return e.sections[itemUpdate.sectionIndex];
  });
};

export const saveInspectionSectionItemProperties = ({ dispatch }, itemUpdate) => {
  log.debug('actions:saveInspectionSectionItemProperties', itemUpdate);

  let update = {
    updateInspectionTemplate: itemUpdate.updateInspectionTemplate,
    newInspectionTemplateProperties: {
      sections: JSON.parse(JSON.stringify(itemUpdate.updateInspectionTemplate.sections)),
    },
    reason: itemUpdate.reason,
  };

  if (!(itemUpdate.sectionIndex >= 0)) {
    itemUpdate.sectionIndex = itemUpdate.updateInspectionTemplate.sections.findIndex(
      (i) => i._id === itemUpdate.inspectionSectionId
    );
    if (itemUpdate.sectionIndex === -1) {
      throw new Error(
        `inspectionSectionId ${itemUpdate.inspectionSectionId} not found in collection ${JSON.stringify(
          itemUpdate.updateInspectionTemplate.sections,
          null,
          ' '
        )})`
      );
    }
  }

  if (itemUpdate.hasOwnProperty('newIndex')) {
    update.newInspectionTemplateProperties.sections[itemUpdate.sectionIndex].items.splice(
      itemUpdate.newIndex,
      0,
      update.newInspectionTemplateProperties.sections[itemUpdate.sectionIndex].items.splice(itemUpdate.itemIndex, 1)[0]
    );
    for (let i = 0; i < update.newInspectionTemplateProperties.sections[itemUpdate.sectionIndex].items.length; i++) {
      update.newInspectionTemplateProperties.sections[itemUpdate.sectionIndex].items[i] = Object.assign(
        {},
        update.newInspectionTemplateProperties.sections[itemUpdate.sectionIndex].items[i],
        { sort: i }
      );
    }
  } else {
    update.newInspectionTemplateProperties.sections[itemUpdate.sectionIndex].items[itemUpdate.itemIndex] =
      Object.assign(
        {},
        inspectionTemplateApi.getDefaultInspectionSectionItem(),
        itemUpdate.updateInspectionTemplate.sections[itemUpdate.sectionIndex].items[itemUpdate.itemIndex],
        itemUpdate.newInspectionSectionItemProperties
      );
  }
  return dispatch('saveInspectionTemplateProperties', update).then((e) => {
    return e.sections[itemUpdate.sectionIndex];
  });
};

export const saveInspectionTemplateProperties = ({ commit, state }, update) => {
  log.debug('actions:saveInspectionTemplateProperties', update);

  if (!editingAllowed(state)) {
    return Promise.resolve();
  }

  let logReason = logAction(
    { commit, state },
    {
      project: state.currentProject,
      object: update.updateInspectionTemplate,
      reason: 'Updated InspectionTemplate Properties',
      update: update.newInspectionTemplateProperties,
    }
  );

  setInspectionTemplateProperties({ commit, state }, update);

  return inspectionTemplateApi
    .saveInspectionTemplate(update.updateInspectionTemplate, update.reason || logReason)
    .then((savedInspectionTemplate) => {
      update.newInspectionTemplateProperties = savedInspectionTemplate;

      commit(types.setInspectionTemplateProperties, update);
      return savedInspectionTemplate;
    })
    .catch((e) => {
      log.error('Error when saving inspectionTemplate', e);
      commit(types.setSyncStatus, { status: 'Error', live: 0, message: 'Database Error occurred', count: 1 });
    });
};

export const setInspectionTemplateProperties = ({ commit, state }, update) => {
  if (!editingAllowed(state)) {
    return;
  }

  commit(types.setInspectionTemplateProperties, update);
};

export const syncInspectionTemplate = ({ commit, state }, inspectionTemplate) => {
  if (inspectionTemplate.projectId === state.currentProject._id) {
    let foundItem = state.currentProjectInspectionTemplates.find((i) => {
      return i._id === inspectionTemplate._id;
    });

    if (foundItem) {
      commit(types.setInspectionTemplateProperties, {
        updateInspectionTemplate: foundItem,
        newInspectionTemplateProperties: inspectionTemplate,
      });
    } else {
      commit(types.addInspectionTemplate, { newInspectionTemplate: inspectionTemplate });
    }
  }
};



// WEBPACK FOOTER //
// ./src/store/inspection/inspectionTemplateActions.js